/**
 * Composant pour gérer le dessin de zones de prospection sur la carte
 * Permet de dessiner des polygones point par point avec des lignes droites
 */

import React, { useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import type { Coordinate, ProspectionZone } from '../../types/prospectionZone';
import { ZONE_COLOR_HEX } from '../../types/prospectionZone';
import { eventsService } from '../../services/events.service';
import { darkenColor } from '../../utils/colorUtils';
import logger from '../../utils/logger';

interface ZoneDrawingProps {
  map: mapboxgl.Map | null;
  isDrawing: boolean;
  zones: ProspectionZone[];
  selectedZoneId?: string | null;
  onPointAdded?: (point: Coordinate) => void;
  onDrawingComplete?: (coordinates: Coordinate[]) => void;
}

export const ZoneDrawing: React.FC<ZoneDrawingProps> = ({
  map,
  isDrawing,
  zones,
  selectedZoneId,
  onPointAdded,
  onDrawingComplete
}) => {
  const drawingPoints = useRef<Coordinate[]>([]);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const linesLayerId = useRef<string>('drawing-lines');
  const isDrawingRef = useRef<boolean>(isDrawing);
  const currentPopupRef = useRef<mapboxgl.Popup | null>(null);
  
  // Mettre à jour la ref quand isDrawing change
  useEffect(() => {
    isDrawingRef.current = isDrawing;
  }, [isDrawing]);

  // Fonction pour calculer le centre d'une zone (centroïde)
  const calculateZoneCenter = useCallback((coordinates: Coordinate[]): [number, number] => {
    if (coordinates.length === 0) return [0, 0];
    
    let sumLng = 0;
    let sumLat = 0;
    
    coordinates.forEach(([lng, lat]) => {
      sumLng += lng;
      sumLat += lat;
    });
    
    return [sumLng / coordinates.length, sumLat / coordinates.length];
  }, []);

  // Fonction pour formater la date
  const formatDate = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  }, []);

  // Fonction pour ouvrir le popup d'une zone
  const openZonePopup = useCallback((zoneId: string) => {
    if (!map) return;
    
    // Fermer le popup précédent s'il existe
    if (currentPopupRef.current) {
      currentPopupRef.current.remove();
      currentPopupRef.current = null;
    }
    
    // Trouver la zone
    const zone = zones.find(z => z.id === zoneId);
    if (!zone || !zone.isVisible) return;
    
    // Calculer le centre de la zone
    const center = calculateZoneCenter(zone.coordinates);
    
    // Créer le contenu HTML du popup
    const popupContent = `
      <div style="padding: 12px; min-width: 200px;">
        <div style="font-weight: 600; font-size: 15px; margin-bottom: 8px; color: ${ZONE_COLOR_HEX[zone.color]};">
          ${zone.name}
        </div>
        ${zone.description ? `
          <div style="margin-bottom: 8px; font-size: 13px; color: #666; line-height: 1.4;">
            ${zone.description}
          </div>
        ` : ''}
        <div style="font-size: 12px; color: #999; border-top: 1px solid #eee; padding-top: 6px; margin-top: 6px;">
          📅 Créée le ${formatDate(zone.createdAt)}
        </div>
      </div>
    `;
    
    // Créer et afficher le popup
    const popup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      maxWidth: '300px',
      anchor: 'bottom',
      offset: 25
    })
      .setLngLat(center)
      .setHTML(popupContent)
      .addTo(map);
    
    // Stocker la référence
    currentPopupRef.current = popup;
    
    // Centrer la carte sur la zone
    map.flyTo({
      center: center,
      zoom: Math.max(map.getZoom(), 14),
      duration: 1000
    });
    
    logger.debug('Popup de zone ouvert:', zone.name);
  }, [map, zones, calculateZoneCenter, formatDate]);

  // Nettoyer les markers et lignes temporaires
  const cleanupDrawing = useCallback(() => {
    // Supprimer les markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Supprimer la couche de lignes UNIQUEMENT si la map est chargée
    if (map && map.loaded && map.loaded() && map.getLayer && map.getSource) {
      try {
        if (map.getLayer(linesLayerId.current)) {
          map.removeLayer(linesLayerId.current);
        }
        if (map.getSource(linesLayerId.current)) {
          map.removeSource(linesLayerId.current);
        }
      } catch (error) {
        console.warn('⚠️ Erreur lors du nettoyage des couches:', error);
      }
    }

    drawingPoints.current = [];
  }, [map]);

  // Mettre à jour les lignes de dessin
  const updateDrawingLines = useCallback(() => {
    if (!map || drawingPoints.current.length < 2) return;

    const sourceId = linesLayerId.current;

    // Créer les lignes entre les points
    // Si on a 3+ points, fermer le polygone visuellement
    const lineCoordinates = drawingPoints.current.length >= 3 
      ? [...drawingPoints.current, drawingPoints.current[0]] // Fermer le polygone
      : [...drawingPoints.current];

    const geojson: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: lineCoordinates
      }
    };

    // Mettre à jour ou créer la source
    if (map.getSource(sourceId)) {
      (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(geojson);
    } else {
      map.addSource(sourceId, {
        type: 'geojson',
        data: geojson
      });

      map.addLayer({
        id: sourceId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': '#007bff',
          'line-width': 2,
          'line-dasharray': [2, 2]
        }
      });
    }
  }, [map]);

  // Fonction réutilisable pour fermer le polygone
  const closePolygon = useCallback((markerElement?: HTMLElement) => {
    if (drawingPoints.current.length < 3) return;
    
    const firstPoint = drawingPoints.current[0];
    const finalCoordinates = [...drawingPoints.current, firstPoint];
    logger.debug('Polygone fermé', { points: finalCoordinates.length });
    
    // Changer le premier marker en vert
    if (markerElement) {
      markerElement.style.backgroundColor = '#28a745';
      markerElement.style.boxShadow = '0 2px 6px rgba(40, 167, 69, 0.5)';
    } else if (markersRef.current.length > 0) {
      const firstMarkerElement = markersRef.current[0].getElement();
      firstMarkerElement.style.backgroundColor = '#28a745';
      firstMarkerElement.style.boxShadow = '0 2px 6px rgba(40, 167, 69, 0.5)';
    }
    
    onDrawingComplete?.(finalCoordinates);
  }, [onDrawingComplete]);

  // Ajouter un point de dessin
  const addDrawingPoint = useCallback((lngLat: mapboxgl.LngLat) => {
    const coordinate: Coordinate = [lngLat.lng, lngLat.lat];
    logger.debug('Point cliqué', { coordinate, totalPoints: drawingPoints.current.length });
    
    // Vérifier si on clique sur le premier point (pour fermer le polygone)
    if (drawingPoints.current.length >= 3) {
      const firstPoint = drawingPoints.current[0];
      const distance = Math.sqrt(
        Math.pow(coordinate[0] - firstPoint[0], 2) +
        Math.pow(coordinate[1] - firstPoint[1], 2)
      );

      // Si on est proche du premier point (environ 50m), fermer le polygone
      if (distance < 0.0005) {
        closePolygon();
        return;
      }
    }

    // Ajouter le point
    drawingPoints.current.push(coordinate);
    onPointAdded?.(coordinate);

    // Créer un marker pour ce point
    const el = document.createElement('div');
    el.className = 'zone-drawing-marker';
    
    // Premier point : rouge et plus gros (indique "cliquez ici pour fermer")
    if (drawingPoints.current.length === 1) {
      el.style.cssText = `
        width: 16px;
        height: 16px;
        background-color: #dc3545;
        border: 3px solid white;
        border-radius: 50%;
        cursor: pointer;
        box-shadow: 0 2px 6px rgba(220, 53, 69, 0.5);
        z-index: 1000;
      `;
      
      // Ajouter un gestionnaire de clic sur le premier marker pour fermer la zone
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        logger.debug('🎯 Clic sur le premier marker pour fermer la zone');
        closePolygon(el);
      });
      
      // Support tactile pour mobile
      el.addEventListener('touchend', (e) => {
        e.preventDefault();
        e.stopPropagation();
        logger.debug('📱 Touch sur le premier marker pour fermer la zone');
        closePolygon(el);
      });
    } else {
      // Autres points : bleu
      el.style.cssText = `
        width: 12px;
        height: 12px;
        background-color: #007bff;
        border: 2px solid white;
        border-radius: 50%;
        cursor: pointer;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      `;
    }

    const marker = new mapboxgl.Marker({ element: el })
      .setLngLat([coordinate[0], coordinate[1]])
      .addTo(map!);

    markersRef.current.push(marker);

    // Mettre à jour les lignes
    updateDrawingLines();
  }, [map, onPointAdded, cleanupDrawing, updateDrawingLines, closePolygon]);

  // Gérer les clics sur la carte en mode dessin
  useEffect(() => {
    logger.debug('🔄 useEffect mode dessin:', { hasMap: !!map, isDrawing });
    
    if (!map || !isDrawing) {
      if (map && !isDrawing) {
        logger.debug('🧹 Nettoyage du dessin (mode dessin désactivé)');
        cleanupDrawing();
      }
      return;
    }

    logger.debug('🎨 Mode dessin activé - activation du curseur');
    const canvas = map.getCanvas();
    if (!canvas) return;

    // Changer le curseur
    canvas.style.cursor = 'crosshair';

    const handleClick = (e: mapboxgl.MapMouseEvent) => {
      logger.debug('🖱️ Clic détecté en mode dessin');
      e.preventDefault();
      addDrawingPoint(e.lngLat);
    };

    map.on('click', handleClick);

    return () => {
      map.off('click', handleClick);
      const canvas = map.getCanvas();
      if (canvas) {
        canvas.style.cursor = '';
      }
    };
  }, [map, isDrawing, addDrawingPoint, cleanupDrawing]);

  // Écouter les changements de visibilité
  useEffect(() => {
    logger.debug('🔔 ZoneDrawing - Abonnement à ZONE_VISIBILITY_CHANGED');
    
    // Map pour stocker les handlers par zone (pour le cleanup)
    const eventHandlers = new Map<string, {
      mouseenter: () => void;
      mouseleave: () => void;
      click: () => void;
    }>();

    const handleVisibilityChange = (data: { zoneId: string; isVisible: boolean }) => {
      logger.debug('👁️ ZoneDrawing - Événement ZONE_VISIBILITY_CHANGED reçu:', data);
      
      if (!map || !map.loaded()) return;
      
      const fillId = `zone-fill-${data.zoneId}`;
      const lineId = `zone-line-${data.zoneId}`;
      
      // Nettoyer les anciens event listeners de cette zone
      const oldHandlers = eventHandlers.get(data.zoneId);
      if (oldHandlers && map.getLayer(fillId)) {
        try {
          map.off('mouseenter', fillId, oldHandlers.mouseenter);
          map.off('mouseleave', fillId, oldHandlers.mouseleave);
          map.off('click', fillId, oldHandlers.click);
          eventHandlers.delete(data.zoneId);
        } catch (error) {
          // Ignorer si la couche n'existe plus
        }
      }
      
      // Si la zone doit être masquée, supprimer les couches
      if (!data.isVisible) {
        try {
          // Supprimer les couches dans le bon ordre
          if (map.getLayer(lineId)) map.removeLayer(lineId);
          if (map.getLayer(fillId)) map.removeLayer(fillId);
          if (map.getSource(fillId)) map.removeSource(fillId);
          logger.debug('✅ Zone masquée:', data.zoneId);
        } catch (error) {
          logger.error('Erreur lors du masquage de la zone:', error);
        }
      } else {
        // Si la zone doit être affichée, la redessiner
        const zone = zones.find(z => z.id === data.zoneId);
        if (zone) {
          try {
            // Supprimer d'abord si existe (dans le bon ordre)
            if (map.getLayer(lineId)) map.removeLayer(lineId);
            if (map.getLayer(fillId)) map.removeLayer(fillId);
            if (map.getSource(fillId)) map.removeSource(fillId);
            
            const geojson: GeoJSON.Feature<GeoJSON.Polygon> = {
              type: 'Feature',
              properties: { name: zone.name, color: zone.color },
              geometry: { type: 'Polygon', coordinates: [zone.coordinates] }
            };
            
            map.addSource(fillId, { type: 'geojson', data: geojson });
            
            const isSelected = zone.id === selectedZoneId;
            const zoneColor = zone.color ? darkenColor(ZONE_COLOR_HEX[zone.color]) : darkenColor(ZONE_COLOR_HEX.blue);
            
            map.addLayer({
              id: fillId,
              type: 'fill',
              source: fillId,
              paint: {
                'fill-color': zoneColor,
                'fill-opacity': isSelected ? 0.3 : 0.2
              }
            });
            
            map.addLayer({
              id: lineId,
              type: 'line',
              source: fillId,
              paint: {
                'line-color': zoneColor,
                'line-width': isSelected ? 3 : 2
              }
            });
            
            // Créer les handlers pour pouvoir les nettoyer plus tard
            const handleMouseEnter = () => {
              if (!isDrawingRef.current) {
                map.getCanvas().style.cursor = 'pointer';
              }
            };

            const handleMouseLeave = () => {
              if (!isDrawingRef.current) {
                map.getCanvas().style.cursor = '';
              }
            };

            const handleClick = () => {
              openZonePopup(zone.id);
            };
            
            // Stocker les handlers pour le cleanup
            eventHandlers.set(data.zoneId, {
              mouseenter: handleMouseEnter,
              mouseleave: handleMouseLeave,
              click: handleClick
            });
            
            // Ajouter les gestionnaires d'événements
            map.on('mouseenter', fillId, handleMouseEnter);
            map.on('mouseleave', fillId, handleMouseLeave);
            map.on('click', fillId, handleClick);
            
            logger.debug('✅ Zone affichée:', data.zoneId);
          } catch (error) {
            logger.error('Erreur lors de l\'affichage de la zone:', error);
          }
        }
      }
    };
    
    const unsubscribe = eventsService.subscribe('ZONE_VISIBILITY_CHANGED', handleVisibilityChange, 'zone-drawing-visibility');
    
    // Cleanup : nettoyer tous les event listeners au démontage
    return () => {
      unsubscribe();
      
      // Nettoyer tous les event listeners de toutes les zones
      eventHandlers.forEach((handlers, zoneId) => {
        const fillId = `zone-fill-${zoneId}`;
        try {
          if (map && map.getLayer(fillId)) {
            map.off('mouseenter', fillId, handlers.mouseenter);
            map.off('mouseleave', fillId, handlers.mouseleave);
            map.off('click', fillId, handlers.click);
          }
        } catch (error) {
          // Ignorer les erreurs si la couche n'existe plus
        }
      });
      eventHandlers.clear();
    };
  }, [map, zones, selectedZoneId, openZonePopup]);

  // Afficher les zones existantes
  useEffect(() => {
    logger.debug('🔍 useEffect zones déclenché:', {
      hasMap: !!map,
      isLoaded: map?.loaded?.(),
      zonesCount: zones.length
    });
    
    if (!map) {
      logger.debug('⏳ Map pas encore disponible');
      return;
    }

    // Fonction pour afficher les zones
    const displayZones = () => {
      logger.debug('🗺️ Tentative d\'affichage des zones existantes:', zones.length);
      
      // Vérifier que la map est vraiment prête
      try {
        // Test si la map est vraiment prête en essayant d'accéder à une propriété
        const style = map.getStyle();
        if (!style) {
          logger.debug('⏳ Map style pas encore chargé, abandon');
          return;
        }
      } catch (error) {
        logger.debug('⏳ Map pas encore prête (erreur getStyle), abandon');
        return;
      }

      logger.debug('✅ Map confirmée prête, affichage des zones');

      // Nettoyer TOUTES les couches de zones existantes (y compris celles supprimées)
      try {
        const style = map.getStyle();
        if (style && style.layers) {
          const zoneLayers = style.layers.filter(layer => 
            layer.id.startsWith('zone-fill-') || layer.id.startsWith('zone-line-')
          );
          
          zoneLayers.forEach(layer => {
            try {
              if (map.getLayer(layer.id)) {
                map.removeLayer(layer.id);
              }
            } catch (error) {
              // Ignorer les erreurs
            }
          });
          
          // Supprimer aussi les sources
          if (style.sources) {
            Object.keys(style.sources).forEach(sourceId => {
              if (sourceId.startsWith('zone-fill-')) {
                try {
                  if (map.getSource(sourceId)) {
                    map.removeSource(sourceId);
                  }
                } catch (error) {
                  // Ignorer les erreurs
                }
              }
            });
          }
        }
      } catch (error) {
        console.warn('⚠️ Erreur lors du nettoyage des couches de zones:', error);
      }

      // Ajouter seulement les zones visibles (is_visible = true)
      const zonesToDisplay = zones.filter(zone => zone.isVisible);
      logger.debug('Ajout des zones visibles sur la carte', { visible: zonesToDisplay.length, total: zones.length });
      
      zonesToDisplay.forEach(zone => {
        const fillId = `zone-fill-${zone.id}`;
        const lineId = `zone-line-${zone.id}`;
        const isSelected = zone.id === selectedZoneId;
        logger.debug('Ajout zone', { name: zone.name, fillId });

        // Créer le polygone
      const geojson: GeoJSON.Feature<GeoJSON.Polygon> = {
        type: 'Feature',
        properties: {
          name: zone.name,
          color: zone.color
        },
        geometry: {
          type: 'Polygon',
          coordinates: [zone.coordinates]
        }
      };

      // Ajouter la source
      map.addSource(fillId, {
          type: 'geojson',
          data: geojson
        });

        // Ajouter le remplissage
        map.addLayer({
          id: fillId,
          type: 'fill',
          source: fillId,
          paint: {
            'fill-color': darkenColor(ZONE_COLOR_HEX[zone.color]),
            'fill-opacity': isSelected ? 0.4 : 0.25
          }
        });

        // Ajouter le contour
        map.addLayer({
          id: lineId,
          type: 'line',
          source: fillId,
          paint: {
            'line-color': darkenColor(ZONE_COLOR_HEX[zone.color]),
            'line-width': isSelected ? 3 : 2
          }
        });

        // Ajouter un popup au survol (seulement si pas en mode dessin)
        map.on('mouseenter', fillId, () => {
          if (!isDrawingRef.current) {
            map.getCanvas().style.cursor = 'pointer';
          }
        });

        map.on('mouseleave', fillId, () => {
          if (!isDrawingRef.current) {
            map.getCanvas().style.cursor = '';
          }
        });

        map.on('click', fillId, () => {
          // Utiliser la fonction openZonePopup au lieu de créer un popup inline
          openZonePopup(zone.id);
        });
      });
    };

    // Appeler displayZones immédiatement si la map est prête
    if (map.loaded && map.loaded()) {
      logger.debug('✅ Map prête, affichage immédiat des zones');
      displayZones();
    } else {
      logger.debug('⏳ Map pas encore prête, attente de l\'événement idle...');
      // Attendre que la map soit vraiment prête avec l'événement 'idle'
      // Utilisation de 'once' pour éviter les appels multiples
      const handleIdle = () => {
        logger.debug('✅ Map idle, affichage des zones');
        displayZones();
      };
      
      map.once('idle', handleIdle);
      
      // Cleanup
      return () => {
        map.off('idle', handleIdle);
      };
    }
  }, [map, zones, selectedZoneId]);

  // Écouter l'événement OPEN_ZONE_POPUP depuis la sidebar
  useEffect(() => {
    if (!map) return;

    const handleOpenZonePopup = (data: { zoneId: string }) => {
      if (!data.zoneId) return;
      logger.debug('📡 Événement OPEN_ZONE_POPUP reçu pour la zone:', data.zoneId);
      openZonePopup(data.zoneId);
    };

    const unsubscribe = eventsService.subscribe('OPEN_ZONE_POPUP', handleOpenZonePopup, 'zone-drawing');

    return () => {
      unsubscribe();
    };
  }, [map, openZonePopup]);

  return null; // Ce composant ne rend rien visuellement
};

export default ZoneDrawing;
